import React from 'react';
import { List, ListItemText, ListItemButton, Tooltip, Typography } from '@mui/material';
import { faStar } from '@fortawesome/pro-light-svg-icons';
import { faStar as faStarFavorite } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { amber } from '@mui/material/colors';

export interface FavoriteDropdownTestIds {
  list: string;
  item: string;
  itemText: string;
  favoriteItem: string;
  favoriteIcon: string;
}

export const defaultFavoriteDropdownTestIds: FavoriteDropdownTestIds = {
  list: 'FavoriteDropdownList',
  item: 'FavoriteDropdownItem',
  itemText: 'FavoriteDropdownItemText',
  favoriteItem: 'FavoriteDropdownFavoriteItem',
  favoriteIcon: 'FavoriteDropdownFavoriteIcon',
};

export interface FavoriteDropdownListProps<T> {
  items: T[];
  favoriteItemId: string | undefined;
  selectedItemId?: string | undefined;
  onSelectItem: (item: T) => void;
  onToggleFavorite: (item: T) => void;
  getItemId: (item: T) => string | undefined;
  getItemLabel: (item: T) => string;
  testIds?: FavoriteDropdownTestIds;
  minWidth?: number;
  sortItems?: (a: T, b: T) => number;
}

export function FavoriteDropdownList<T>(props: FavoriteDropdownListProps<T>) {
  const {
    items,
    favoriteItemId,
    selectedItemId,
    onSelectItem,
    onToggleFavorite,
    getItemId,
    getItemLabel,
    testIds = defaultFavoriteDropdownTestIds,
    minWidth = 350,
    sortItems,
  } = props;

  const sortedItems = [...items].sort(sortItems ?? ((a, b) => getItemLabel(a).localeCompare(getItemLabel(b))));

  function handleToggleFavorite(item: T, event: React.MouseEvent) {
    event.stopPropagation();
    onToggleFavorite(item);
  }

  return (
    <List
      data-testid={testIds.list}
      component="nav"
      sx={{
        width: '100%',
        minWidth,
        backgroundColor: 'white',
      }}
    >
      {sortedItems.map((item, index) => {
        const itemId = getItemId(item);
        const isFavorite = itemId ? itemId === favoriteItemId : false;
        const isSelected = itemId ? itemId === selectedItemId : false;
        const label = getItemLabel(item);

        return (
          <ListItemButton
            data-testid={isFavorite ? testIds.favoriteItem : testIds.item}
            key={itemId ?? `${label}-${index}`}
            sx={{ padding: '5px 0px' }}
            aria-label={label}
            onClick={() => onSelectItem(item)}
          >
            <FontAwesomeIcon
              style={{ margin: '0px 5px 3px 10px', cursor: 'pointer' }}
              onClick={event => handleToggleFavorite(item, event)}
              icon={isFavorite ? faStarFavorite : faStar}
              size="xs"
              color={isFavorite ? amber[700] : ''}
              data-testid={testIds.favoriteIcon}
            />
            <Tooltip className="textTooltip" enterNextDelay={400} title={label} disableInteractive>
              <ListItemText
                data-testid={testIds.itemText}
                disableTypography
                primary={
                  <Typography
                    noWrap
                    style={{
                      fontSize: '0.8571428571428571rem',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    {label}
                  </Typography>
                }
              />
            </Tooltip>
          </ListItemButton>
        );
      })}
    </List>
  );
}
